/**
 * Typeface pairings, shared by the console that renders them and the API that
 * validates them.
 *
 * In `src/shared/` and not in `app/lib/theme.ts` because BOTH sides need it and
 * `src/api/` may never import from `src/app/` (see the isolation rules in
 * CLAUDE.md). Restating the list on the API side instead would let it accept a
 * key the stylesheet has no rules for, the first time one of the two was edited
 * alone.
 */

/**
 * Typeface pairings a company can choose from.
 *
 * A CURATED LIST, not a free-text field, for two reasons and the first is not
 * taste. The chosen value ends up in a stylesheet URL and in a `font-family`
 * declaration; an allowlist means neither can ever carry anything a customer
 * typed. Validation on a font name is not something to hand-roll.
 *
 * The second reason is that a display face and a body face have to work
 * TOGETHER, and asking a cleaning-company owner to pair them from a list of
 * 1,600 Google fonts is asking them to do a job they did not sign up for. Each
 * row here is a pairing that already works, described by what it feels like
 * rather than by its name.
 *
 * `stack` always ends in a real system fallback, so a company's console still
 * renders correctly if Google Fonts is blocked, slow, or down.
 */
export const FONT_CHOICES = {
  classic: {
    label: 'Classic',
    hint: 'Warm serif headings over a clean sans. The default.',
    display: "'Lora', Georgia, 'Times New Roman', serif",
    body: "'Instrument Sans', -apple-system, system-ui, sans-serif",
    google: 'family=Lora:ital,wght@0,400..700;1,400..700&family=Instrument+Sans:ital,wght@0,400..700;1,400..700',
  },
  editorial: {
    label: 'Editorial',
    hint: 'High-contrast serif. Considered, a little formal.',
    display: "'Fraunces', Georgia, serif",
    body: "'Figtree', -apple-system, system-ui, sans-serif",
    google: 'family=Fraunces:ital,opsz,wght@0,9..144,400..700;1,9..144,400..700&family=Figtree:ital,wght@0,400..700;1,400..700',
  },
  modern: {
    label: 'Modern',
    hint: 'One geometric sans throughout. Plain and current.',
    display: "'Outfit', -apple-system, system-ui, sans-serif",
    body: "'Outfit', -apple-system, system-ui, sans-serif",
    google: 'family=Outfit:wght@300..700',
  },
  grounded: {
    label: 'Grounded',
    hint: 'Sturdy slab headings. Reads as practical and hands-on.',
    display: "'Roboto Slab', Georgia, serif",
    body: "'Inter', -apple-system, system-ui, sans-serif",
    google: 'family=Roboto+Slab:wght@400..700&family=Inter:ital,wght@0,400..700;1,400..700',
  },
  system: {
    label: 'System',
    hint: 'Whatever the device already uses. Loads nothing, fastest.',
    display: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
    body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
    google: null,
  },
} as const;

export type FontKey = keyof typeof FONT_CHOICES;

export const DEFAULT_FONT: FontKey = 'classic';

export function isFontKey(value: unknown): value is FontKey {
  return typeof value === 'string' && Object.hasOwn(FONT_CHOICES, value);
}

/**
 * The Google Fonts stylesheet URL for a pairing, or null for 'system'.
 *
 * Built from the allowlisted `google` fragment above and nothing else, so no
 * customer-supplied text reaches this URL.
 */
export function fontStylesheetHref(font: FontKey): string | null {
  const chosen = FONT_CHOICES[font];
  return chosen.google ? `https://fonts.googleapis.com/css2?${chosen.google}&display=swap` : null;
}
